import React, { FC, useMemo, useState } from 'react';
import { Crucible, ERC20Token, RewardProgram } from 'types';
import { Box } from '@chakra-ui/layout';
import { useCruciblesLocks } from 'hooks/query/useCrucibles';
import { useRewardPrograms } from 'store/zustand/useRewardPrograms';
import { useCrucibles } from 'hooks/query/useCrucibles';
import {
  Heading,
  Flex,
  Spinner,
  Text,
  useMediaQuery,
  Stack,
  Tag,
  Icon,
  Alert,
  AlertIcon,
  Button
} from '@chakra-ui/react';
import { Table, Thead, Tbody, Tr, Th, Td } from 'components/table';
import UnlockedAssetsAction from './UnlockedAssetsAction';
import LockedAssetsAction from './LockedAssetsAction';
import formatNumber from 'utils/formatNumber';
import TokenIcon from 'components/shared/TokenIcon';
import { useCrucibleAssets } from 'hooks/query/useCrucibleAssets';
import { GiPirateFlag } from 'react-icons/gi';
import { GoReport } from 'react-icons/go';
import { convertConnectorTypeToWalletName } from 'utils/convertConnectorTypeToWalletName';
import { getBrowser } from 'helpers/browser';
import { getDevice } from 'helpers/device';
import { useWeb3React } from 'hooks/web3';

type Props = {
  crucible: Crucible;
  setTabIndex: React.Dispatch<React.SetStateAction<number>>;
  setTransferTabIndex: React.Dispatch<React.SetStateAction<number>>;
  tokenBlacklist: string[];
};

const Summary: FC<Props> = ({
  crucible,
  setTabIndex,
  setTransferTabIndex,
  tokenBlacklist
}) => {
  const { isIdle, isLoading } = useCrucibles();
  const { status: lockStatus, data: crucibleLocks } = useCruciblesLocks();
  const { rewardPrograms } = useRewardPrograms();
  const { account, connector, chainId = 1 } = useWeb3React();
  const [isLargerThan768] = useMediaQuery('(min-width: 769px)');
  const [reporting, setReporting] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccessReport] = useState('');
  const [tokensReported, setTokensReported] = useState<string[]>([]);

  const { data: crucibleAssets, status: assetsStatus } = useCrucibleAssets(
    crucible?.id
  );

  const selectedCrucibleLocks = useMemo(() => {
    if (crucible?.id && crucibleLocks && crucibleLocks[crucible.id]) {
      return crucibleLocks[crucible.id];
    }
    return [];
  }, [crucible, crucibleLocks]);
  const handleReport = async (asset: ERC20Token) => {
    setReporting(asset.address);
    try {
      const connectorName = convertConnectorTypeToWalletName(connector);
      const browser = getBrowser();
      const device = getDevice();

      const response = await fetch(
        '/api/post-phishing-report?' +
          new URLSearchParams({
            crucibleId: crucible.id,
            tokenAddress: asset.address,
            tokenSymbol: asset.symbol,
            chainId: chainId.toString(),
            address: account || '',
            wallet: connectorName || '',
            device,
            browser
          })
      );

      if (!response.ok) {
        throw new Error(
          'Report failed, please try again or reach out to team directly.'
        );
      }

      setError('');
      setSuccessReport(
        'Report for ' + asset.symbol + ' successfully submitted.'
      );
      setTokensReported((tokensReported) => [...tokensReported, asset.address]);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setReporting('');
    }
  };

  if (
    lockStatus === 'idle' ||
    lockStatus === 'loading' ||
    assetsStatus === 'idle' ||
    assetsStatus === 'loading' ||
    isLoading ||
    isIdle
  ) {
    return (
      <Box p={[4, 8]} borderRadius='xl'>
        <Spinner />
      </Box>
    );
  }

  if (!crucible || !crucible.id || !crucibleAssets) {
    return (
      <Box p={[4, 8]} borderRadius='xl'>
        Something went wrong, please try refreshing the page.
      </Box>
    );
  }

  return (
    <Flex flexDir='column'>
      <Heading size='md' fontWeight='500' textAlign='left'>
        Locked assets
      </Heading>
      <Box
        mt={6}
        mb={10}
        borderWidth={1}
        borderRadius='xl'
        borderColor='gray.700'
        overflow='hidden'
      >
        {lockStatus === 'error' ? (
          <Text textAlign='left' color='gray.300' p={4}>
            Failed to get locked assets
          </Text>
        ) : selectedCrucibleLocks.length === 0 ? (
          <Text textAlign='left' color='gray.300' p={4}>
            You have no locked assets
          </Text>
        ) : (
          <Table size='md'>
            <Thead bg='gray.700'>
              <Tr>
                <Th>Asset in Crucible</Th>
                <Th>Amount</Th>
                <Th>Subscribed to</Th>
                <Th textAlign='right'>Menu</Th>
              </Tr>
            </Thead>
            <Tbody>
              {selectedCrucibleLocks?.map((lock, idx) => {
                const rewardProgram = rewardPrograms.find(
                  (p) =>
                    p.address.toLowerCase() ===
                    lock.rewardProgramAddress.toLowerCase()
                );
                return (
                  <Tr key={idx}>
                    <Td
                      bg={isLargerThan768 ? 'none' : 'gray.700'}
                      width={isLargerThan768 ? '240px' : 'none'}
                    >
                      <Stack direction='row' alignItems='center'>
                        <TokenIcon asset={lock.subscribedToken} />
                        <Text maxW='184px' isTruncated>
                          {lock.subscribedToken.symbol}
                        </Text>
                      </Stack>
                    </Td>
                    <Td>
                      {formatNumber.token(
                        lock.subscribedToken.value.amount,
                        lock.subscribedToken.decimals
                      )}
                    </Td>
                    <Td>{rewardProgram?.name}</Td>
                    <Td
                      width={isLargerThan768 ? '160px' : 'none'}
                      textAlign='right'
                    >
                      <LockedAssetsAction
                        lock={lock}
                        rewardProgram={rewardProgram as RewardProgram}
                        crucibleId={crucible.id}
                        setTabIndex={setTabIndex}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>
      <Heading size='md' fontWeight='500' textAlign='left'>
        Unlocked assets
      </Heading>
      <Box
        mt={6}
        borderWidth={1}
        borderRadius='xl'
        borderColor='gray.700'
        overflow='hidden'
      >
        {assetsStatus === 'error' ? (
          <Text textAlign='left' color='gray.300' p={4}>
            Failed to get unlocked assets
          </Text>
        ) : crucibleAssets.length === 0 ? (
          <Text textAlign='left' color='gray.300' p={4}>
            You have no unlocked assets
          </Text>
        ) : (
          <Table size='md'>
            <Thead bg='gray.700'>
              <Tr>
                <Th width={['auto', 'auto', '240px']}>Asset in Crucible</Th>
                <Th>Amount</Th>
                <Th width={['auto', 'auto', '160px']} textAlign='right'>
                  Menu
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {crucibleAssets.map((asset) => (
                <Tr key={asset.address}>
                  <Td bg={['gray.700', null, 'none']}>
                    <Stack direction='row' alignItems='center'>
                      <TokenIcon asset={asset} />
                      <Text maxW='184px' isTruncated>
                        {asset.symbol}
                      </Text>
                      {tokenBlacklist.some((token) => {
                        return (
                          token.toLowerCase() === asset.address.toLowerCase()
                        );
                      }) ? (
                        <Tag colorScheme='red' minWidth='unset'>
                          <Text display={['none', null, 'unset']}>
                            Phish/Scam
                          </Text>
                          <Icon ml={[0, null, 2]} as={GiPirateFlag} />
                        </Tag>
                      ) : (
                        <Button
                          variant='outline'
                          borderRadius={10}
                          color='gray.200'
                          size='xs'
                          colorScheme='gray'
                          onClick={() => handleReport(asset)}
                          isLoading={reporting === asset.address}
                          disabled={
                            reporting !== '' ||
                            tokensReported.includes(asset.address)
                          }
                        >
                          <Text display={['none', null, 'unset']}>Report</Text>
                          <Icon
                            display={['unset', null, 'none']}
                            ml={[0, null, 2]}
                            as={GoReport}
                          />
                        </Button>
                      )}
                    </Stack>
                  </Td>
                  <Td>
                    {formatNumber.token(asset.value.amount, asset.decimals)}
                  </Td>
                  <Td textAlign='right'>
                    <UnlockedAssetsAction
                      crucibleAddress={crucible.id}
                      asset={asset}
                      setTabIndex={setTabIndex}
                      setTransferTabIndex={setTransferTabIndex}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
      {error && (
        <Alert status='error' mt={4} borderRadius='xl'>
          <AlertIcon />
          {error}
        </Alert>
      )}
      {success && (
        <Alert status='success' mt={4} borderRadius='xl'>
          <AlertIcon />
          {success}
        </Alert>
      )}
    </Flex>
  );
};

export default Summary;
